import React, { useContext, useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { Alert, Button, IconButton } from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Warning,
  Info,
  Build,
} from "@mui/icons-material";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../services/use-auth";
import UserContext from "../assets/user_context";

// Debug logging - set to true to enable verbose logging
const DEBUG_LOGGING = false;
const debugLog = (...args) => DEBUG_LOGGING && console.log(...args);

// Banner auto-opens once per local calendar day, then stays collapsed across
// navigations (the component remounts per-route via with_subscription.jsx)
const BANNER_AUTO_OPEN_KEY = "t3_subBanner_lastAutoOpenDate";

const getLocalDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Separate countdown component to avoid re-rendering the main banner
const MaintenanceCountdown = ({ scheduledDateTime }) => {
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    if (!scheduledDateTime) {
      setCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const timeDiff = scheduledDateTime - now;

      if (timeDiff <= 0) {
        setCountdown(null);
        return;
      }

      const hours = Math.floor(timeDiff / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      setCountdown(
        `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
      );
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [scheduledDateTime]);

  return countdown || "0:00:00";
};

const BannerWrapper = styled("div")(({ theme, isCollapsed }) => ({
  position: "fixed",
  top: 0,
  right: theme.spacing(3),
  zIndex: 1000,
  width: isCollapsed ? "70px" : "450px",
  backgroundColor:
    theme.palette.mode === "light"
      ? "rgba(255, 255, 255, 0.98)"
      : "rgba(0, 0, 0, 0.98)",
  backdropFilter: "blur(10px)",
  borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
  border: `1px solid ${theme.palette.divider}`,
  borderTop: "none",
  boxShadow:
    theme.palette.mode === "light"
      ? "0 4px 12px rgba(0, 0, 0, 0.12)"
      : "0 4px 12px rgba(0, 0, 0, 0.4)",
  transition: "width 0.3s ease-in-out",
  overflow: "hidden",
}));

const CollapsedContent = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(1),
  paddingRight: theme.spacing(2),
  minHeight: "48px",
}));

const BannerContent = styled(Alert)(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "transparent",
  border: "none",
  borderRadius: 0,
  padding: theme.spacing(1.5),
  paddingRight: theme.spacing(4),
  "& .MuiAlert-message": {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
    fontSize: "0.875rem",
    paddingRight: theme.spacing(1),
  },
  "& .MuiAlert-icon": {
    alignItems: "flex-start",
    paddingTop: "2px",
    marginRight: theme.spacing(1),
  },
  "& .MuiAlert-action": {
    padding: 0,
    margin: 0,
  },
}));

const MessageRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  width: "100%",
}));

const CollapseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  padding: theme.spacing(0.5),
  backgroundColor:
    theme.palette.mode === "light"
      ? "rgba(0, 0, 0, 0.04)"
      : "rgba(255, 255, 255, 0.08)",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.12)",
  },
}));

const SubscriptionBanner = () => {
  const { subscriptionData, setOpenSubscribe, orgData } =
    useContext(UserContext);
  const { db } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Read-only here: start collapsed if the banner already auto-opened
    // today. The key is written in an effect below, only when the
    // subscription banner is actually shown expanded — so a maintenance
    // countdown or a null render doesn't burn the day's auto-open.
    try {
      return localStorage.getItem(BANNER_AUTO_OPEN_KEY) === getLocalDateString();
    } catch {
      return false;
    }
  });
  const [daysRemaining, setDaysRemaining] = useState(0);

  // Maintenance notification state
  const [scheduledMaintenance, setScheduledMaintenance] = useState(null);
  const [isWithinTwoHours, setIsWithinTwoHours] = useState(false);

  // Calculate days remaining in trial
  React.useEffect(() => {
    if (orgData && subscriptionData?.freeTrial) {
      const today = new Date();

      // Check for custom expiration date first
      if (subscriptionData.freeTrialExpirationDate) {
        debugLog(
          "🔍 Using custom expiration date:",
          subscriptionData.freeTrialExpirationDate,
        );
        const expirationDate = subscriptionData.freeTrialExpirationDate.toDate
          ? subscriptionData.freeTrialExpirationDate.toDate()
          : new Date(subscriptionData.freeTrialExpirationDate);
        const remaining = Math.max(
          0,
          Math.floor((expirationDate - today) / (1000 * 60 * 60 * 24)),
        );
        debugLog("📅 Custom trial days remaining:", remaining);
        setDaysRemaining(remaining);
      } else {
        // Default 15-day trial from creation date
        debugLog("🔍 Checking org creation date:", orgData.creationDate);

        if (!orgData.creationDate) {
          debugLog(
            "⚠️ No creation date found for org, defaulting to 0 days remaining",
          );
          setDaysRemaining(0);
          return;
        }

        const creationDate = orgData.creationDate?.toDate
          ? orgData.creationDate.toDate()
          : new Date(orgData.creationDate);

        // Check if date is valid
        if (isNaN(creationDate.getTime())) {
          debugLog("⚠️ Invalid creation date:", orgData.creationDate);
          setDaysRemaining(0);
          return;
        }

        const daysSinceCreation = Math.floor(
          (today - creationDate) / (1000 * 60 * 60 * 24),
        );
        const remaining = Math.max(0, 15 - daysSinceCreation);
        debugLog(
          "📅 Default trial - days since creation:",
          daysSinceCreation,
          "days remaining:",
          remaining,
        );
        setDaysRemaining(remaining);
      }
    } else {
      debugLog(
        "🔍 Not showing trial banner - orgData:",
        !!orgData,
        "freeTrial:",
        subscriptionData?.freeTrial,
      );
    }
  }, [orgData, subscriptionData]);

  // Listener for scheduled maintenance
  useEffect(() => {
    if (!db) return;

    const maintenanceRef = doc(db, "system", "maintenance");
    const unsubscribe = onSnapshot(maintenanceRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();

        if (data.scheduledDateTime && !data.enabled) {
          const scheduledTime = data.scheduledDateTime.toDate
            ? data.scheduledDateTime.toDate()
            : new Date(data.scheduledDateTime);

          setScheduledMaintenance({
            scheduledDateTime: scheduledTime,
            message:
              data.maintenanceMessage ||
              "Scheduled maintenance will begin shortly.",
          });

          // Check if within 2 hours
          const timeDiff = scheduledTime - new Date();
          setIsWithinTwoHours(timeDiff <= 2 * 60 * 60 * 1000 && timeDiff > 0);
        } else {
          setScheduledMaintenance(null);
          setIsWithinTwoHours(false);
        }
      } else {
        setScheduledMaintenance(null);
        setIsWithinTwoHours(false);
      }
    });

    return () => unsubscribe();
  }, [db]);

  // Check if user has active subscription or is in free trial
  const hasSubscription = subscriptionData?.subscriptionCurrent;
  const isFreeTrial = subscriptionData?.freeTrial;

  // Staged downgrade grace window (§6.6) — live countdown until effectiveAt
  const pendingDowngrade = subscriptionData?.pendingDowngrade;
  const downgradeEffectiveAt = pendingDowngrade?.effectiveAt
    ? pendingDowngrade.effectiveAt.toDate
      ? pendingDowngrade.effectiveAt.toDate()
      : new Date(pendingDowngrade.effectiveAt)
    : null;
  const showDowngradeCountdown =
    downgradeEffectiveAt && downgradeEffectiveAt > new Date();

  // Determine what to show - maintenance takes priority when within 2 hours,
  // then the downgrade grace countdown, then trial/subscription state
  const showMaintenanceCountdown = scheduledMaintenance && isWithinTwoHours;
  const showSubscriptionBanner =
    !showMaintenanceCountdown &&
    (showDowngradeCountdown ||
      (hasSubscription && isFreeTrial) ||
      (!hasSubscription && subscriptionData));

  // Urgent maintenance countdown is exempt from the daily auto-open collapse
  useEffect(() => {
    if (showMaintenanceCountdown) {
      setIsCollapsed(false);
    }
  }, [showMaintenanceCountdown]);

  // Mark today's auto-open as consumed once the subscription banner is
  // actually visible expanded; subsequent mounts (navigation) start collapsed
  useEffect(() => {
    if (showSubscriptionBanner && !isCollapsed) {
      try {
        localStorage.setItem(BANNER_AUTO_OPEN_KEY, getLocalDateString());
      } catch {
        // localStorage unavailable — banner just re-opens next navigation
      }
    }
  }, [showSubscriptionBanner, isCollapsed]);

  // Trial-org de-collision (04 §4.2): a trial org (org-level `trial: true`) is
  // governed solely by the TrialBanner — never stack the billing free-trial /
  // subscribe banner on top of it. (This `trial` is distinct from
  // subscriptionData.freeTrial, which is the legacy billing free-trial.)
  if (orgData?.trial === true) {
    return null;
  }

  // Don't show banner if no maintenance countdown and no subscription issues
  if (!showMaintenanceCountdown && !showSubscriptionBanner) {
    return null;
  }

  const handleSubscribeClick = () => {
    setOpenSubscribe(true);
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Determine message, severity, and icon based on what we're showing
  let message, severity, SeverityIcon;

  if (showMaintenanceCountdown) {
    message = (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
        }}>
        <span>🚨 MAINTENANCE IN</span>
        <span
          style={{
            fontFamily: "monospace",
            fontSize: "1.1rem",
            fontWeight: "bold",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            padding: "2px 6px",
            borderRadius: "4px",
          }}>
          <MaintenanceCountdown
            scheduledDateTime={scheduledMaintenance?.scheduledDateTime}
          />
        </span>
        <span>- Save your work now!</span>
      </div>
    );
    severity = "error";
    SeverityIcon = Build;
  } else if (showDowngradeCountdown) {
    const daysLeft = Math.max(
      1,
      Math.ceil((downgradeEffectiveAt - new Date()) / (1000 * 60 * 60 * 24)),
    );
    const isCancellation = pendingDowngrade.reason === "cancellation";
    message = `Your plan ${isCancellation ? "ends" : "changes"} in ${daysLeft} day${daysLeft === 1 ? "" : "s"} (${downgradeEffectiveAt.toLocaleDateString()}). Until then nothing changes — upgrade to keep your features.`;
    severity = "warning";
    SeverityIcon = Warning;
  } else {
    message = isFreeTrial
      ? `You're currently on a free trial (${daysRemaining} days remaining). Some features may be limited.`
      : "Your subscription is not current. Some functionality will be limited.";
    severity = isFreeTrial ? "info" : "warning";
    SeverityIcon = isFreeTrial ? Info : Warning;
  }

  return (
    <BannerWrapper isCollapsed={isCollapsed}>
      <CollapseButton
        onClick={handleToggleCollapse}
        size="small"
        aria-label={isCollapsed ? "Expand banner" : "Collapse banner"}>
        {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
      </CollapseButton>

      {isCollapsed ? (
        <CollapsedContent>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "2px",
            }}>
            <SeverityIcon color={severity} fontSize="small" />
            {showMaintenanceCountdown && (
              <span
                style={{
                  fontSize: "0.7rem",
                  fontFamily: "monospace",
                  fontWeight: "bold",
                  color: severity === "error" ? "#d32f2f" : "#666",
                }}>
                <MaintenanceCountdown
                  scheduledDateTime={scheduledMaintenance?.scheduledDateTime}
                />
              </span>
            )}
          </div>
        </CollapsedContent>
      ) : (
        <BannerContent severity={severity} icon={<SeverityIcon />}>
          <MessageRow>
            <span style={{ flex: 1 }}>{message}</span>
            {!showMaintenanceCountdown && (
              <Button
                variant="contained"
                size="small"
                color={isFreeTrial ? "primary" : "warning"}
                onClick={handleSubscribeClick}
                sx={{
                  minWidth: "120px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}>
                {showDowngradeCountdown
                  ? "Keep my features"
                  : isFreeTrial
                    ? "View Plans"
                    : "Subscribe"}
              </Button>
            )}
          </MessageRow>
        </BannerContent>
      )}
    </BannerWrapper>
  );
};

export default SubscriptionBanner;
